import {
    NOTIFICATION_CATALOG,
    NOTIFICATION_SETTINGS_GROUPS,
    NOTIFICATION_SETTINGS_SECTIONS,
    type NotificationSettingsArea,
    type NotificationTypeKey,
} from '@apps/web/lib/notification_catalog.generated';
import { type NotificationChannel } from '@apps/web/lib/notification_channel_features';
import { isNotificationChannelSupportedForType } from '@apps/web/lib/notification_channel_preferences';
import { type components } from '@libraries/typescript/api_client/generated_api_types';

export type NotificationSettingsEmailPriority =
    components['schemas']['ActivityEventPriority'];

export type NotificationSettingsEmailPriorityKey =
    'activity_action_required_email_min_priority';

export type NotificationSettingsItem = {
    readonly control: NotificationSettingsItemControl;
    readonly key: NotificationTypeKey;
    readonly label: string;
    readonly helperText: string;
    readonly keys: ReadonlyArray<NotificationTypeKey>;
};

export type NotificationSettingsItemControl =
    | { readonly kind: 'switch' }
    | {
          readonly channel: 'email';
          readonly defaultPriority: NotificationSettingsEmailPriority;
          readonly kind: 'email-priority';
          readonly priorityKey: NotificationSettingsEmailPriorityKey;
      };

export type NotificationSettingsItemControlPatch = Partial<
    Record<
        NotificationSettingsEmailPriorityKey,
        NotificationSettingsEmailPriority
    >
>;

type NotificationSettingsItemControlPatchAction =
    | {
          readonly action: 'set-priority';
          readonly priority: NotificationSettingsEmailPriority | null;
      }
    | {
          readonly action: 'toggle';
          readonly currentPriorities?: Partial<
              Record<
                  NotificationSettingsEmailPriorityKey,
                  NotificationSettingsEmailPriority | null
              >
          >;
          readonly enabled: boolean;
      };

export type NotificationSettingsSection = {
    readonly title: string;
    /** The PRD's page-level heading this section sits under. */
    readonly area: NotificationSettingsArea;
    readonly items: ReadonlyArray<NotificationSettingsItem>;
};

export type NotificationSettingsAreaGroup = {
    readonly title: NotificationSettingsArea;
    readonly sections: ReadonlyArray<NotificationSettingsSection>;
};

type NotificationSettingsVisibility = {
    readonly planOfCareDutyMonitoringEnabled: boolean;
};

type PlanOfCareDutyMonitoringSettings = {
    readonly plan_of_care_duty_monitoring_available?: boolean | null;
    readonly plan_of_care_duty_monitoring_enabled?: boolean | null;
};

export type NotificationSettingsGroupCheckedState = boolean | 'indeterminate';

export const DEFAULT_NOTIFICATION_SETTINGS_ITEM_CONTROL: NotificationSettingsItemControl =
    {
        kind: 'switch',
    };

const ITEM_CONTROL_OVERRIDES: Partial<
    Record<NotificationTypeKey, NotificationSettingsItemControl>
> = {
    activity_action_required: {
        channel: 'email',
        defaultPriority: 'low',
        kind: 'email-priority',
        priorityKey: 'activity_action_required_email_min_priority',
    },
};

export type NotificationSettingsEnableWarning = {
    readonly title: string;
    readonly description: string;
};

const VOICE_CALL_ENABLE_WARNING: NotificationSettingsEnableWarning = {
    title: 'Turn on voice call notifications?',
    description:
        'Phoebe will call your notification number and read the notification details aloud, including when leaving voicemail. Enabling many notification types can lead to frequent calls.',
};

const ENABLE_WARNINGS: Partial<
    Record<NotificationTypeKey, NotificationSettingsEnableWarning>
> = {
    inbound_sms_received: {
        title: 'Turn on incoming text notifications?',
        description:
            'This turns on notifications for every incoming text message, including routine replies. It can create a high volume of alerts and make important updates easier to miss.',
    },
};

/** Warning policy follows delivery channels and event keys, independent of layout. */
export function getNotificationSettingsEnableWarning(
    channel: NotificationChannel,
    newlyEnabledKeys: ReadonlyArray<NotificationTypeKey>,
): NotificationSettingsEnableWarning | undefined {
    if (channel === 'voice' && newlyEnabledKeys.length > 0) {
        return VOICE_CALL_ENABLE_WARNING;
    }
    return newlyEnabledKeys
        .map((key) => ENABLE_WARNINGS[key])
        .find((warning) => warning !== undefined);
}

export function isPlanOfCareDutyMonitoringEnabled(
    generalSettings: PlanOfCareDutyMonitoringSettings | null | undefined,
): boolean {
    return (
        generalSettings?.plan_of_care_duty_monitoring_available === true &&
        generalSettings.plan_of_care_duty_monitoring_enabled === true
    );
}

export function getNotificationSettingsGroupCheckedState(
    keys: ReadonlyArray<NotificationTypeKey>,
    isEnabled: (key: NotificationTypeKey) => boolean,
): NotificationSettingsGroupCheckedState {
    const enabledCount = keys.filter(isEnabled).length;
    if (enabledCount === 0) {
        return false;
    }
    return enabledCount === keys.length ? true : 'indeterminate';
}

export function notificationSettingsItemControlPatch(
    control: NotificationSettingsItemControl,
    action: NotificationSettingsItemControlPatchAction,
): NotificationSettingsItemControlPatch {
    switch (control.kind) {
        case 'email-priority': {
            if (action.action === 'set-priority') {
                return action.priority === null
                    ? {}
                    : { [control.priorityKey]: action.priority };
            }
            return action.enabled
                ? {
                      [control.priorityKey]:
                          action.currentPriorities?.[control.priorityKey] ??
                          control.defaultPriority,
                  }
                : {};
        }
        case 'switch':
            return {};
        default: {
            const exhaustiveControl: never = control;
            return exhaustiveControl;
        }
    }
}

/** The rows of a section that a channel can actually deliver. */
export function getNotificationSectionDeliverableItems(
    section: NotificationSettingsSection,
    channel: NotificationChannel,
): ReadonlyArray<NotificationSettingsItem> {
    return section.items.filter((item) =>
        isNotificationChannelSupportedForType(item.key, channel),
    );
}

/**
 * Every preference key a section-level switch writes for one channel: the
 * full key group of each row the channel can deliver. Empty when nothing in
 * the section reaches that channel, in which case no switch renders.
 */
export function getNotificationSectionChannelKeys(
    section: NotificationSettingsSection,
    channel: NotificationChannel,
): ReadonlyArray<NotificationTypeKey> {
    return getNotificationSectionDeliverableItems(section, channel).flatMap(
        (item) => item.keys,
    );
}

/**
 * The state of a section-level switch: on when every deliverable row is on
 * for the channel, off when none is, mixed otherwise. A row whose grouped
 * keys disagree counts as on, matching what its own switch shows.
 */
export function getNotificationSectionChannelCheckedState(
    section: NotificationSettingsSection,
    channel: NotificationChannel,
    isEnabled: (key: NotificationTypeKey) => boolean,
): NotificationSettingsGroupCheckedState {
    const rowStates = getNotificationSectionDeliverableItems(
        section,
        channel,
    ).map((item) =>
        getNotificationSettingsGroupCheckedState(item.keys, isEnabled),
    );
    if (rowStates.every((state) => state === false)) {
        return false;
    }
    return rowStates.every((state) => state === true) ? true : 'indeterminate';
}

export function getVisibleNotificationSettingsSections({
    planOfCareDutyMonitoringEnabled,
}: NotificationSettingsVisibility): ReadonlyArray<NotificationSettingsSection> {
    return NOTIFICATION_SETTINGS_SECTIONS.map((section) => ({
        title: section.title,
        area: section.area,
        items: section.rows
            .filter(
                (key) =>
                    key !== 'plan_of_care_incomplete' ||
                    planOfCareDutyMonitoringEnabled,
            )
            .map((key) => ({
                control:
                    ITEM_CONTROL_OVERRIDES[key] ??
                    DEFAULT_NOTIFICATION_SETTINGS_ITEM_CONTROL,
                key,
                label: NOTIFICATION_CATALOG[key].label,
                helperText: NOTIFICATION_CATALOG[key].helperText ?? '',
                keys: NOTIFICATION_SETTINGS_GROUPS[key] ?? [key],
            })),
    })).filter((section) => section.items.length > 0);
}

/**
 * Groups sections under their PRD area, keeping section order. Areas with no
 * section (for example, after filtering to what a channel can deliver) are
 * omitted, so a page never shows an empty heading.
 */
export function groupNotificationSettingsSectionsByArea(
    sections: ReadonlyArray<NotificationSettingsSection>,
): ReadonlyArray<NotificationSettingsAreaGroup> {
    const groups: Array<{
        readonly title: NotificationSettingsArea;
        readonly sections: Array<NotificationSettingsSection>;
    }> = [];
    for (const section of sections) {
        const group = groups.find((entry) => entry.title === section.area);
        if (group) {
            group.sections.push(section);
        } else {
            groups.push({ title: section.area, sections: [section] });
        }
    }
    return groups;
}
